import type { VariablesOf } from '@graphql-typed-document-node/core';
import type { PlaidHandler, PlaidLinkOnEventMetadata } from 'react-plaid-link';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { PlaidLinkStableEvent } from 'react-plaid-link';
import useScript from 'react-script-hook';
import PlaidLinkDefaultButton from 'src/components/net-worth/views/plaid-link-default-button';
import { PLAID_OAUTH_AUTH_LOCAL_STORAGE_KEY, PLAID_OAUTH_AUTH_REDIRECT_PAGE } from 'src/constants/account-link';
import { UpdateType } from 'src/constants/portfolio';
import { graphql } from 'src/gql';
import PlaidIcon from 'src/icons/plaid-icon';
import { PlaidErrorCode } from 'src/types/plaid';

import type { ButtonProps } from '@savvy-kit/components/button';
import type { PlaidProduct } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';
import { primaryColors } from '@savvy/deprecated-shared/theme';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { useHouseholdWriteAccess } from '../../hooks/use-household-write-access';
import TrackedButton from '../tracked/tracked-button';

export const CREATE_LINK_TOKEN = graphql(/* GraphQL */ `
  mutation CreatePlaidLinkTokenFromPlaidLinkButton($createLinkTokenInput: CreatePlaidLinkTokenInput!) {
    createPlaidLinkToken(input: $createLinkTokenInput) {
      linkToken
    }
  }
`);

export const CREATE_PROVIDER_ACCOUNT_FROM_PLAID_LINK_BUTTON = graphql(/* GraphQL */ `
  mutation CreateProviderAccountFromPlaidLinkButton($createProviderAccountInput: CreateProviderAccountInput!) {
    createProviderAccount(input: $createProviderAccountInput) {
      providerAccount {
        id
        accessToken
        providerName
      }
    }
  }
`);

export const UPDATE_PROVIDER_ACCOUNT = graphql(/* GraphQL */ `
  mutation UpdateProviderAccountFromPlaidLinkButton($updateProviderAccountInput: UpdateProviderAccountInput!) {
    updateProviderAccount(input: $updateProviderAccountInput) {
      providerAccount {
        id
      }
    }
  }
`);

const PLAID_LINK_STABLE_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

type PlaidLinkButtonProps = {
  readonly products?: PlaidProduct[];
  readonly title: string;
  readonly subtitle?: string;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly onError?: (metadata: PlaidLinkOnEventMetadata) => void;
  readonly accountFilters?: string;
  readonly setIsLoading: (isLoading: boolean) => void;
  readonly buttonProps?: ButtonProps;
  readonly providerAccountId?: ID;
  readonly clientUserId?: ID;
};

export default function PlaidLinkButton({
  products,
  title,
  subtitle,
  onSuccess,
  onError,
  accountFilters,
  setIsLoading,
  buttonProps,
  providerAccountId,
  clientUserId,
}: PlaidLinkButtonProps) {
  const [createLinkToken, { error: createLinkTokenError }] = useMutationWithErrorLogging(CREATE_LINK_TOKEN);
  const [isLoadingPlaid, setIsLoadingPlaid] = useState(false);
  const { canWrite, tooltip } = useHouseholdWriteAccess();
  useScript({
    src: PLAID_LINK_STABLE_URL,
    checkForExisting: true,
  });
  const createProviderAccountMutation = useMutationWithErrorLogging(CREATE_PROVIDER_ACCOUNT_FROM_PLAID_LINK_BUTTON);
  const [updateProviderAccount] = useMutationWithErrorLogging(UPDATE_PROVIDER_ACCOUNT);
  const [createProviderAccount, { error: createProviderAccountError }] = createProviderAccountMutation;

  function createNewLinkedAccount(plaidFlow: PlaidHandler, public_token: string) {
    createProviderAccount({
      variables: {
        createProviderAccountInput: {
          plaidPublicToken: public_token,
          source: 'PLAID',
          userId: clientUserId?.toString(),
        },
      },
    })
      .then((res) => {
        plaidFlow.destroy();
        return onSuccess({
          type: UpdateType.CREATE_PROVIDER_ACCOUNT,
          id: res.data?.createProviderAccount?.providerAccount.id ?? '',
        });
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
    setIsLoading(true);
  }

  function updateExistingLinkedAccount(plaidFlow: PlaidHandler, public_token: string) {
    updateProviderAccount({
      variables: {
        updateProviderAccountInput: {
          // @ts-expect-error - FIXME: This is using the wrong type
          id: providerAccountId ?? '',
          plaidPublicToken: public_token,
        },
      },
    })
      .then((res) => {
        plaidFlow.destroy();
        return onSuccess({
          type: UpdateType.UPDATE_PROVIDER_ACCOUNT,
          id: res.data?.updateProviderAccount?.providerAccount.id ?? '',
        });
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
    setIsLoading(true);
  }

  function onClick() {
    if (isLoadingPlaid || !canWrite) {
      return;
    }
    setIsLoadingPlaid(true);
    const createLinkTokenInput: VariablesOf<typeof CREATE_LINK_TOKEN>['createLinkTokenInput'] = {};
    if (providerAccountId) {
      createLinkTokenInput.providerAccountId = providerAccountId.toString();
    } else {
      createLinkTokenInput.products = products;
    }

    if (accountFilters) {
      createLinkTokenInput.accountFilters = accountFilters;
    }
    createLinkToken({
      variables: {
        createLinkTokenInput,
      },
    })
      .then((res) => {
        const token = res.data?.createPlaidLinkToken?.linkToken;
        if (!token) {
          setIsLoadingPlaid(false);
          return;
        }
        localStorage.setItem(PLAID_OAUTH_AUTH_LOCAL_STORAGE_KEY, token);
        localStorage.setItem(PLAID_OAUTH_AUTH_REDIRECT_PAGE, location.href);
        const plaidFlow = window.Plaid.create({
          token,
          onLoad: () => {
            setIsLoadingPlaid(false);
          },
          onExit: () => {
            setIsLoading(false);
          },
          onEvent: (eventName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- PlaidErrorCode is a string
              eventName === PlaidLinkStableEvent.ERROR &&
              metadata.error_code === PlaidErrorCode.MFA_NOT_SUPPORTED
            ) {
              if (onError) onError(metadata);
            }
          },
          onSuccess: (publicToken) => {
            if (providerAccountId) {
              updateExistingLinkedAccount(plaidFlow, publicToken);
            } else {
              createNewLinkedAccount(plaidFlow, publicToken);
            }
            setIsLoading(false);
          },
        });
        plaidFlow.open();
      })
      .catch((e: unknown) => {
        console.error(e);
      });
    setIsLoading(true);
  }

  function renderButton() {
    // Custom MUI button
    if (buttonProps) {
      return (
        <TrackedButton
          elementName="plaid-link-button"
          {...buttonProps}
          onClick={onClick}
          isLoading={isLoadingPlaid}
          disabled={buttonProps.disabled || !canWrite}
        >
          {title}
        </TrackedButton>
      );
      // Default
    }
    return (
      <PlaidLinkDefaultButton
        onClick={onClick}
        loading={isLoadingPlaid}
        icon={<PlaidIcon color={primaryColors.contrastText} />}
        title={title}
        subtitle={subtitle}
        disabled={!canWrite}
      />
    );
  }

  function renderWithTooltip() {
    if (canWrite || tooltip === null) {
      return renderButton();
    }
    return (
      <Tooltip>
        <TooltipTrigger render={<span data-testid="plaid-link-button-gated" />}>{renderButton()}</TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      {createLinkTokenError && <InputErrorMessage message={createLinkTokenError.message} />}
      {createProviderAccountError && <InputErrorMessage message={createProviderAccountError.message} />}

      {renderWithTooltip()}
    </>
  );
}
